/**
 * Company selection  component.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { GridOptions } from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { hide_company_selector, log_out } from '@rpt-ngrx/actions/session.action';
import {
    selectAccessTokenSet,
    selectCompanies,
    selectCompanyId,
    selectUser,
    selectUserToken,
    showCompanySelector,
} from '@rpt-ngrx/selectors/session.selector';
import { IToolbarButtonDefinition } from '@shared/types';
import { ICompany } from '@shared/types/session';

@Component({
    selector: 'dm-company-selection',
    templateUrl: 'company-selection.component.html',
    styleUrls: ['company-selection.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySelectionComponent implements OnInit, OnDestroy {
    readonly trailDelays = Array.from({ length: 19 }, (_, i) => (i + 1) * 0.02);

    getTrailFill(index: number): string {
        const intensity = Math.round(90 - (index * 60) / 18);
        return `color-mix(in srgb, var(--primary-color) ${intensity}%, white)`;
    }

    @Input() public companiesList: Array<ICompany>;
    @Input() public currentCompanyId: string;
    @Input() set showCompanySelector(value: boolean) {
        this._showModal = value && this.companiesList.length > 0;
        if (!this._showModal) {
            this.isLoading = false;
        }
    }
    @Output() public selectedItem = new EventEmitter();

    public _showModal: boolean = false;
    public selectedItemFromList: any;
    public isLoading: boolean = false;
    public title: string = 'Select a company:';

    public user$: Observable<any>;
    public companies$: Observable<any[]>;
    public accessTokenSet$: Observable<boolean>;
    public currentCompanyId$: Observable<string>;
    public showCompanySelector$: Observable<boolean>;

    public buttons: IToolbarButtonDefinition[] = [
        {
            caption: 'Done',
            value: 'done',
            disabled: true,
            class: 'btn-primary pull-right',
            click: () => this.selectCompany(),
        },
        {
            caption: 'Cancel',
            value: 'cancel',
            class: 'btn-default pull-right',
            click: () => this.closeCompanySelector(),
        },
    ];
    public headerNames: any[] = [
        { headerName: 'Company name', field: 'name', unSortIcon: true },
        { headerName: 'Number of sites', field: 'sites.length', unSortIcon: true },
    ];
    public listGridOptions: GridOptions = {
        rowSelection: 'single',
        suppressCellFocus: true,
        onGridSizeChanged: () => this.resizeGrid(),
        onModelUpdated: () => this.resizeGrid(),
        onRowClicked: (event) => this.selectItem(event.data),
        onRowDoubleClicked: () => this.selectCompany(),
    };

    private token$: Observable<string>;
    private unsubscribe$: Subject<void> = new Subject();

    constructor(
        private store: Store,
        private cdRef: ChangeDetectorRef,
    ) {}

    public ngOnInit() {
        this.companies$ = this.store.select(selectCompanies);
        this.currentCompanyId$ = this.store.select(selectCompanyId);
        this.showCompanySelector$ = this.store.select(showCompanySelector);
        this.user$ = this.store.select(selectUser);
        this.accessTokenSet$ = this.store.select(selectAccessTokenSet);
        this.token$ = this.store.select(selectUserToken);
        this.token$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(() => !!this.selectedItemFromList),
            )
            .subscribe(() => this.resetForm());
    }

    public selectItem(data) {
        this.selectedItemFromList = data;
        this.buttons.find((b) => b.caption === 'Done').disabled = !this.selectedItemFromList;
    }

    public selectCompany() {
        if (this.selectedItemFromList) {
            this.isLoading = true;
            this.cdRef.markForCheck();
            this.selectedItem.emit(this.selectedItemFromList.id);
        }
    }

    public onListQuickFilterChanged(event) {
        if (this.listGridOptions.api) {
            this.listGridOptions.api.setQuickFilter(event.target.value);
        }
    }

    public closeCompanySelector() {
        if (!this.currentCompanyId) {
            this.store.dispatch(log_out());
        }
        this.store.dispatch(hide_company_selector());
        this._showModal = false;
        this.resetForm();
    }

    private resetForm() {
        this.buttons.find((b) => b.caption === 'Done').disabled = true;
        this.selectedItemFromList = null;
    }

    private resizeGrid() {
        if (this._showModal) {
            this.listGridOptions?.api?.sizeColumnsToFit();
        }
    }

    public ngOnDestroy(): void {
        this._showModal = false;
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
